import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Megaphone, Users, Phone, Mail, Plus, ArrowRight, RefreshCw,
  CheckCircle, Clock, AlertCircle, PhoneCall, MessageCircle, UserPlus,
  X, MapPin, BookOpen, Calendar, Tag, User, FileText, Eye, Download
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AddLeadDialog from '@/components/center/AddLeadDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pipelineStages = ['new', 'contacted', 'followup', 'converted'];
const stageLabels = {
  new: 'New Leads',
  contacted: 'Contacted',
  followup: 'Follow-up',
  converted: 'Converted'
};
const stageColors = {
  new: 'from-blue-500 to-blue-600',
  contacted: 'from-amber-500 to-amber-600',
  followup: 'from-purple-500 to-purple-600',
  converted: 'from-emerald-500 to-emerald-600'
};

const CenterCRMPage = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, followup: 0, converted: 0 });
  const [addLeadDialogOpen, setAddLeadDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Download Leads as Excel (Name, Course, Contact No, Date + helpful extras).
  const downloadLeadsExcel = () => {
    if (!leads.length) {
      toast.error('Koi lead nahi mila download karne ke liye');
      return;
    }
    const rows = leads.map((l, idx) => ({
      'Sr No': idx + 1,
      'Name': l.name || '',
      'Course': l.course_interested || l.course || '',
      'Contact No': l.mobile || l.phone || '',
      'Date': l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      }) : '',
      'Stage': stageLabels[l.stage] || l.stage || '',
      'Source': l.source || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 6 }, { wch: 22 }, { wch: 24 }, { wch: 14 }, { wch: 14 },
      { wch: 12 }, { wch: 14 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Leads_${today}.xlsx`);
    toast.success(`${rows.length} leads downloaded`);
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/center/leads`, { headers: getAuthHeaders() });
      const data = response.data || [];
      setLeads(data);
      
      // Calculate stats
      setStats({
        total: data.length,
        new: data.filter(l => l.stage === 'new').length,
        contacted: data.filter(l => l.stage === 'contacted').length,
        followup: data.filter(l => l.stage === 'followup').length,
        converted: data.filter(l => l.stage === 'converted').length
      });
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setLeads([]);
      setStats({ total: 0, new: 0, contacted: 0, followup: 0, converted: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Unified handler for when lead is added via AddLeadDialog
  const handleLeadAdded = () => {
    fetchLeads(); // Refresh lead list and stats
  };

  // Open lead detail dialog
  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setLeadDetailOpen(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('hi-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const moveLeadToStage = async (leadId, newStage) => {
    try {
      await axios.put(`${API}/center/leads/${leadId}/stage`, { stage: newStage }, { headers: getAuthHeaders() });
      toast.success(`Lead ${stageLabels[newStage]} me move ho gayi`);
      fetchLeads();
    } catch (error) {
      // Optimistic update
      setLeads(leads.map(l => l.id === leadId ? {...l, stage: newStage} : l));
      toast.success(`Lead ${stageLabels[newStage]} me move ho gayi`);
    }
  };

  // Note: fetchCourses is handled by AddLeadDialog component internally

  const getLeadsByStage = (stage) => leads.filter(l => l.stage === stage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">CRM & Lead Management</h1>
          <p className="text-slate-500 mt-1">{stats.total} total leads in pipeline</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchLeads} variant="outline" className="gap-2" data-testid="crm-refresh-btn">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            onClick={downloadLeadsExcel}
            variant="outline"
            className="gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="crm-download-leads-btn"
            title="Download all leads as Excel"
          >
            <Download className="w-4 h-4" />
            Download
          </Button>
          <Button 
            onClick={() => setAddLeadDialogOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2"
            data-testid="crm-add-lead-btn"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Leads</p>
              <p className="text-xl font-bold text-slate-900">{stats.total}</p>
            </div>
          </div>
        </div>
        {pipelineStages.map(stage => (
          <div key={stage} className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 bg-gradient-to-br ${stageColors[stage]} rounded-lg flex items-center justify-center`}>
                {stage === 'new' && <AlertCircle className="w-5 h-5 text-white" />}
                {stage === 'contacted' && <Phone className="w-5 h-5 text-white" />}
                {stage === 'followup' && <Clock className="w-5 h-5 text-white" />}
                {stage === 'converted' && <CheckCircle className="w-5 h-5 text-white" />}
              </div>
              <div>
                <p className="text-sm text-slate-500">{stageLabels[stage]}</p>
                <p className="text-xl font-bold text-slate-900">{stats[stage]}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline View */}
      <div className="grid md:grid-cols-4 gap-4">
        {pipelineStages.map(stage => (
          <div key={stage} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Stage Header */}
            <div className={`bg-gradient-to-r ${stageColors[stage]} px-4 py-3`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-white">{stageLabels[stage]}</h3>
                <span className="bg-white/20 text-white text-sm px-2 py-0.5 rounded-full">
                  {getLeadsByStage(stage).length}
                </span>
              </div>
            </div>
            
            {/* Leads List */}
            <div className="p-3 space-y-3 min-h-[300px]">
              {getLeadsByStage(stage).length === 0 ? (
                <p className="text-center text-slate-400 py-8 text-sm">No leads</p>
              ) : (
                getLeadsByStage(stage).map(lead => (
                  <motion.div
                    key={lead.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => handleLeadClick(lead)}
                    className="bg-slate-50 rounded-xl p-3 border border-slate-100 hover:border-purple-200 hover:bg-purple-50/50 transition-all cursor-pointer"
                    data-testid={`lead-card-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{lead.name}</p>
                        <p className="text-xs text-slate-500">{lead.course_interested || lead.course || 'No course'}</p>
                      </div>
                      {lead.score && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          lead.score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                          lead.score >= 60 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {lead.score}%
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                      <Phone className="w-3 h-3" />
                      {lead.mobile || lead.phone}
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={(e) => { e.stopPropagation(); window.open(`tel:${lead.mobile || lead.phone}`); }}
                        className="p-1.5 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors"
                        title="Call"
                      >
                        <PhoneCall className="w-3 h-3 text-emerald-600" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleLeadClick(lead); }}
                        className="p-1.5 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-3 h-3 text-blue-600" />
                      </button>
                      {stage !== 'converted' && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); moveLeadToStage(lead.id, pipelineStages[pipelineStages.indexOf(stage) + 1]); }}
                          className="p-1.5 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors ml-auto"
                          title="Move to Next Stage"
                        >
                          <ArrowRight className="w-3 h-3 text-purple-600" />
                        </button>
                      )}
                      {stage === 'converted' && (
                        <button className="p-1.5 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors ml-auto">
                          <UserPlus className="w-3 h-3 text-emerald-600" />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Unified Add Lead Dialog Component */}
      <AddLeadDialog 
        open={addLeadDialogOpen} 
        onOpenChange={setAddLeadDialogOpen}
        onSuccess={handleLeadAdded}
      />

      {/* Lead Detail Dialog */}
      <Dialog open={leadDetailOpen} onOpenChange={setLeadDetailOpen}>
        <DialogContent className="max-w-lg" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Lead Details / लीड विवरण
            </DialogTitle>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-4 mt-4">
              {/* Lead Score Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedLead.stage === 'new' ? 'bg-blue-100 text-blue-700' :
                  selectedLead.stage === 'contacted' ? 'bg-amber-100 text-amber-700' :
                  selectedLead.stage === 'followup' ? 'bg-purple-100 text-purple-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {stageLabels[selectedLead.stage] || selectedLead.stage}
                </span>
                {selectedLead.score && (
                  <span className={`text-lg font-bold px-3 py-1 rounded-full ${
                    selectedLead.score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                    selectedLead.score >= 60 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    Score: {selectedLead.score}%
                  </span>
                )}
              </div>

              {/* Lead Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Name */}
                <div className="col-span-2 bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <User className="w-4 h-4" />
                    Name / नाम
                  </div>
                  <p className="font-semibold text-slate-900 text-lg">{selectedLead.name}</p>
                </div>

                {/* Phone */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Phone className="w-4 h-4" />
                    Phone / फोन
                  </div>
                  <a href={`tel:${selectedLead.mobile || selectedLead.phone}`} className="font-medium text-blue-600 hover:underline">
                    {selectedLead.mobile || selectedLead.phone || 'N/A'}
                  </a>
                </div>

                {/* Email */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Mail className="w-4 h-4" />
                    Email
                  </div>
                  <p className="font-medium text-slate-700 truncate">{selectedLead.email || 'N/A'}</p>
                </div>

                {/* Course Interest */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <BookOpen className="w-4 h-4" />
                    Course / कोर्स
                  </div>
                  <p className="font-medium text-slate-700">{selectedLead.course_interested || selectedLead.course || 'Not specified'}</p>
                </div>

                {/* Source */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Tag className="w-4 h-4" />
                    Source / स्रोत
                  </div>
                  <p className="font-medium text-slate-700 capitalize">{selectedLead.source?.replace('_', ' ') || 'N/A'}</p>
                </div>

                {/* Lead ID */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <FileText className="w-4 h-4" />
                    Lead ID
                  </div>
                  <p className="font-mono text-sm text-slate-700">{selectedLead.lead_id || selectedLead.id?.slice(0, 8) || 'N/A'}</p>
                </div>

                {/* Created Date */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Created / बनाया गया
                  </div>
                  <p className="font-medium text-slate-700 text-sm">{formatDate(selectedLead.created_at)}</p>
                </div>

                {/* Remarks/Notes */}
                {(selectedLead.remarks || selectedLead.notes) && (
                  <div className="col-span-2 bg-amber-50 rounded-lg p-3 border border-amber-100">
                    <div className="flex items-center gap-2 text-amber-600 text-sm mb-1">
                      <FileText className="w-4 h-4" />
                      Notes / नोट्स
                    </div>
                    <p className="text-slate-700">{selectedLead.remarks || selectedLead.notes}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="flex-1 gap-2"
                  onClick={() => window.open(`tel:${selectedLead.mobile || selectedLead.phone}`)}
                >
                  <PhoneCall className="w-4 h-4" />
                  Call
                </Button>
                {selectedLead.stage !== 'converted' && (
                  <Button 
                    className="flex-1 gap-2 bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      const currentIndex = pipelineStages.indexOf(selectedLead.stage);
                      if (currentIndex < pipelineStages.length - 1) {
                        moveLeadToStage(selectedLead.id, pipelineStages[currentIndex + 1]);
                        setLeadDetailOpen(false);
                      }
                    }}
                  >
                    <ArrowRight className="w-4 h-4" />
                    Move to {stageLabels[pipelineStages[pipelineStages.indexOf(selectedLead.stage) + 1]] || 'Next'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterCRMPage;
